
import React, { useEffect, useState } from 'react';
import { FileText, Image, Layers, Zap, Info, Download, Search, Link2, Globe } from 'lucide-react';
import { getPluginDownloadUrl } from '../services/firebase';

const GuideSection: React.FC<{ title: string; children: React.ReactNode; icon: React.ReactNode }> = ({ title, children, icon }) => (
    <div className="bg-white rounded-xl border border-warm/20 p-6 mb-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4 border-b border-warm/10 pb-3">
            <div className="p-2 bg-gray-50 rounded-lg text-dark">{icon}</div>
            <h3 className="font-serif text-lg font-bold text-dark">{title}</h3>
        </div>
        <div className="space-y-4 text-cool text-sm leading-relaxed">
            {children}
        </div>
    </div>
);

const SettingRow: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
    <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-gray-50 last:border-0 gap-1 sm:gap-4">
        <span className="font-semibold text-cool text-xs uppercase tracking-wide">{label}</span>
        <div className="text-right">
            <span className="font-bold text-dark block">{value}</span>
            {note && <span className="text-[10px] text-cool/60 italic">{note}</span>}
        </div>
    </div>
);

const ExportGuide: React.FC = () => {
  const [pluginUrl, setPluginUrl] = useState<string | null>(null);

  useEffect(() => {
    getPluginDownloadUrl().then(url => {
        if (url) setPluginUrl(url);
    });
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
        <div className="mb-10">
            <h1 className="font-serif text-3xl md:text-4xl text-dark mb-3">Optimal Export Settings</h1>
            <p className="text-cool max-w-2xl">
                Every page you upload is automatically re-rendered into fast, optimized web images — so your job is simply
                to hand us a clean source PDF. The viewer now uses a robust engine that reads almost any PDF (InDesign,
                Illustrator, Photoshop, PowerPoint, Excel, Canva — anywhere), so these settings aren't about avoiding
                errors; they just keep your uploads small and your pages loading instantly on mobile.
            </p>
        </div>

        {/* HOW THE VIEWER TREATS YOUR FILE */}
        <div className="bg-warm/10 border border-warm/25 rounded-xl p-5 mb-8 text-sm text-cool grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex gap-2">
                <Image size={18} className="shrink-0 text-dark/70 mt-0.5" />
                <span>We render each page to about <strong className="text-dark">1600px wide</strong>. That means <strong className="text-dark">150 PPI</strong> is the sweet spot — anything higher just bloats the upload with no visible gain.</span>
            </div>
            <div className="flex gap-2">
                <Search size={18} className="shrink-0 text-dark/70 mt-0.5" />
                <span>Keep text as <strong className="text-dark">live text</strong> (don't outline it). Real text powers instant in-booklet search and keeps letters razor sharp.</span>
            </div>
            <div className="flex gap-2">
                <Link2 size={18} className="shrink-0 text-dark/70 mt-0.5" />
                <span>Clickable product links are added <strong className="text-dark">inside the app</strong> with the <em>Add Links</em> tool — they don't need to be in the PDF.</span>
            </div>
        </div>

        {/* PLUGIN DOWNLOAD SECTION */}
        <div className="bg-gradient-to-r from-dark to-gray-800 text-white p-6 md:p-8 rounded-2xl shadow-xl mb-12 flex flex-col md:flex-row items-center justify-between gap-6 border border-white/10">
            <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/4/48/Adobe_InDesign_CC_icon.svg" className="w-8 h-8 rounded shadow-sm" alt="ID" />
                    <h2 className="font-serif text-2xl font-bold">Workflow Automation</h2>
                </div>
                <p className="text-white/80 max-w-xl text-sm md:text-base leading-relaxed">
                    Skip the manual settings entirely. Our official <strong>Adobe InDesign UXP Plugin</strong> applies the
                    ideal export options and uploads straight to your library in one click. (InDesign 18.5+ · Mac &amp; Windows.)
                </p>
            </div>
            <a
                href={pluginUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-shrink-0 px-6 py-3 bg-white text-dark rounded-full font-bold shadow-lg hover:bg-gray-100 transition-all flex items-center gap-2 transform hover:scale-105 active:scale-95 ${!pluginUrl ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={(e) => !pluginUrl && e.preventDefault()}
            >
                <Download size={20} />
                Download Plugin (.ccx)
            </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <GuideSection title="Adobe InDesign" icon={<Layers className="text-[#ff3366]" />}>
                <p className="mb-2 font-medium">File &rsaquo; Export &rsaquo; Adobe PDF (Print)</p>
                <SettingRow label="Compatibility" value="Acrobat 7 (PDF 1.6)" />
                <SettingRow label="Compression" value="JPEG · Bicubic to 150 PPI" note="Use JPEG (not JPEG 2000). Quality: High." />
                <SettingRow label="Color Output" value="Convert to sRGB" note="Matches screens; avoids washed-out CMYK" />
                <SettingRow label="Standard" value="None needed" note="PDF/X is a print spec — not required for web" />
                <div className="bg-emerald-50 p-3 rounded text-xs text-emerald-800 mt-2 flex gap-2">
                    <Info size={14} className="shrink-0 mt-0.5" />
                    Leave <strong>text as live text</strong> (don't outline) so search works. You don't need to include PDF hyperlinks — add clickable links in the app afterwards.
                </div>
            </GuideSection>

            <GuideSection title="Adobe Illustrator" icon={<FileText className="text-[#ff9a00]" />}>
                <p className="mb-2 font-medium">Save As: Adobe PDF</p>
                <SettingRow label="Preset" value="[Smallest File Size]" />
                <SettingRow label="Preserve Editing" value="Unchecked" note="Reduces file size significantly" />
                <SettingRow label="Compression" value="150 PPI · JPEG (Average)" />
                <SettingRow label="Marks & Bleeds" value="None" note="Digital viewing needs no crop marks" />
                <div className="bg-blue-50 p-3 rounded text-xs text-blue-800 mt-2 flex gap-2">
                    <Info size={14} className="shrink-0 mt-0.5" />
                    The viewer handles transparency &amp; drop shadows fine now — only flatten/rasterize a page if you actually spot an artifact.
                </div>
            </GuideSection>

            <GuideSection title="Adobe Photoshop" icon={<Image className="text-[#31a8ff]" />}>
                <p className="mb-2 font-medium">Save As: Photoshop PDF</p>
                <SettingRow label="Image Compression" value="JPEG" />
                <SettingRow label="Quality" value="8 - High" note="Avoid 12 (Maximum) — the app re-compresses anyway" />
                <SettingRow label="Layers" value="Flatten Image" note="Always flatten before saving PDF" />
                <SettingRow label="Color Profile" value="sRGB IEC61966-2.1" />
            </GuideSection>

            <GuideSection title="Acrobat Pro (Optimizer)" icon={<Zap className="text-[#ff0000]" />}>
                <p className="mb-2 font-medium">Already have a big PDF? Save as Other &rsaquo; Optimized PDF</p>
                <SettingRow label="Images" value="JPEG · Bicubic to 150 ppi" />
                <SettingRow label="Transparency" value="Medium Resolution" />
                <SettingRow label="Discard Objects" value="Check All" note="Discard hidden layers & flatten form fields" />
                <SettingRow label="Clean Up" value="Check All" note="Remove invalid links & optimize page content" />
            </GuideSection>
        </div>

        {/* ANY OTHER APP */}
        <GuideSection title="PowerPoint, Excel, Word, Canva & anything else" icon={<Globe className="text-emerald-600" />}>
            <p>
                No special software required. Just <strong className="text-dark">Export / Save As &rsaquo; PDF</strong>. If the app offers a
                colour option choose <strong className="text-dark">sRGB</strong>, and if it offers a quality/size option pick a
                <strong className="text-dark"> standard/medium</strong> web setting rather than the largest print one. That's it — the viewer
                reads it and renders clean web pages automatically. If a page ever comes out blank when it shouldn't, the app flags it
                <em> “Needs review”</em> so you'll know before anyone else does.
            </p>
        </GuideSection>

        <div className="mt-8 bg-dark text-white p-6 rounded-xl shadow-lg">
            <h3 className="font-serif text-xl mb-4 flex items-center gap-2">
                <Zap className="text-yellow-400" size={20} />
                Golden Rules for Web Performance
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
                <div>
                    <h4 className="font-bold mb-2 text-warm">Resolution</h4>
                    <p className="opacity-80">The viewer renders each page to ~1600px wide, so <strong className="text-white">150 PPI</strong> is ideal — crisp on 4K screens, with nothing wasted.</p>
                </div>
                <div>
                    <h4 className="font-bold mb-2 text-warm">Color Space</h4>
                    <p className="opacity-80">Always export in <strong className="text-white">sRGB</strong>. CMYK can look washed-out or neon on monitors.</p>
                </div>
                <div>
                    <h4 className="font-bold mb-2 text-warm">File Size Target</h4>
                    <p className="opacity-80">Aim for &lt; 1MB per page on average — smaller sources upload and process faster. A 20-page catalog under ~20MB is perfect.</p>
                </div>
            </div>
        </div>
    </div>
  );
};

export default ExportGuide;
